import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ActivatedRoute } from '@angular/router';
import { CalcValidators } from '@app/core/directives/validators/calc-validators';
import { InlineResponse20019, Mensaje, MetabolicoService } from '@app/core/services/api';
import { CaseDataService } from '@app/core/services/case-data/case-data.service';
import { ModalService } from '@app/core/services/modal/modal.service';
import { SpinnerService } from '@app/core/services/spinner/spinner.service';
import { FEETS, HEIGHT_MEASURES, INCHS, WEIGHT_MEASURES } from '@app/global/constants';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { forkJoin, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ABDOMINAL_PERIMETER_MEASURES } from '../../global/constants';

@Component({
  selector: 'app-metabolic-calc-form',
  templateUrl: './metabolic-calc-form.component.html',
  styleUrls: ['./metabolic-calc-form.component.scss']
})
export class MetabolicCalcFormComponent implements OnInit, AfterViewInit {
  @Input() data: any;
  @Output() addFormParent = new EventEmitter<FormGroup>();

  heightMetricSystem: string[] = [
    this.translateService.instant('METABOLIC-CALC.SYS-METRIC'),
    this.translateService.instant('METABOLIC-CALC.SYS-IMPERIAL')
  ];
  heightMeasures = HEIGHT_MEASURES;
  weightMeasures = WEIGHT_MEASURES;
  abdominalPerimeterMeasures = ABDOMINAL_PERIMETER_MEASURES;
  feets = FEETS;
  inchs = INCHS;
  tobaccoQuantities: any[] = [];
  vapingQuantities: any[] = [];

  minDate = new Date(new Date().getFullYear() - 110, 0, 1);
  maxDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDay());

  showBirthDate = false;
  birthDate = '';
  caseSuscription!: Subscription;
  basicData: FormGroup = new FormGroup({});
  formReady = false;

  constructor(
    private caseInProgressSrv: CaseDataService,
    private formGroupDirective: FormGroupDirective,
    private spinnerService: SpinnerService,
    private metabolicoService: MetabolicoService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private activatedRouter: ActivatedRoute
  ) {}
  ngOnInit() {
    this.showBirthDate = !this.hasCaseInCourse() && !this.hasCaseInParams();
    const spinner = this.spinnerService.showSpinner();
    forkJoin([this.metabolicoService.smokeGet(), this.metabolicoService.vapingGet()]).subscribe(
      (response: [InlineResponse20019[], InlineResponse20019[]]) => {
        spinner.close();
        this.tobaccoQuantities = [...response[0]];
        this.vapingQuantities = [...response[1]];
        if (this.hasCaseInCourse() && !this.data) {
          const caseInCourse = localStorage.getItem('CASE_IN_COURSE');
          if (caseInCourse) {
            this.initForm({ ...JSON.parse(caseInCourse), birthDate: new Date().toISOString() });
          }
        } else {
          if (this.data) {
            this.initForm(this.data);
          } else {
            this.initForm();
          }
        }
      },
      (error) => {
        spinner.close();
        this.modalService.openModalDanger('Error', error);
      }
    );
  }

  ngAfterViewInit(): void {
    if (this.data && this.basicData) {
      this.basicData.patchValue(this.data);
    }
  }

  compareByValue = (a: any, b: any): boolean => a?.value === b?.value;

  // Method to init form
  initForm(caseData?: any) {
    this.basicData = new FormGroup({
      age: new FormControl(caseData ? { disabled: true, value: caseData.age } : { disabled: true, value: '' }, [Validators.required]),
      gender: new FormControl(caseData ? { disabled: true, value: caseData.gender } : '', [Validators.required]),
      country: new FormControl(caseData ? { disabled: true, value: caseData.country } : '', [
        Validators.required
      ]),
      heightMetricSystem: new FormControl(
        caseData && caseData.heightMetricSystem ? { disabled: false, value: caseData.heightMetricSystem } : this.heightMetricSystem[0]
      ),
      height: new FormControl(caseData ? { disabled: false, value: caseData.height } : '', [CalcValidators.height()]),
      heightMeasure: new FormControl(caseData ? { disabled: false, value: caseData.heightMeasure } : '', []),
      heightFt: new FormControl(caseData ? { disabled: false, value: caseData.heightFt } : '', []),
      heightInch: new FormControl(caseData ? { disabled: false, value: caseData.heightInch } : '', []),
      weight: new FormControl(caseData ? { disabled: false, value: caseData.weight } : '', [CalcValidators.weight()]),
      weightMeasure: new FormControl(caseData ? { disabled: false, value: caseData.weightMeasure } : '', []),
      imc: new FormControl(caseData ? { disabled: true, value: caseData.imc } : { disabled: true, value: '' }, []),
      abdominalPerimeter: new FormControl(caseData ? { disabled: false, value: caseData.abdominalPerimeter } : '', [
        CalcValidators.abdominalPerimeter(0, 0, '')
      ]),
      abdominalPerimeterMeasure: new FormControl(caseData ? { disabled: false, value: caseData.abdominalPerimeterMeasure } : '', []),
      tobacco: new FormControl(caseData ? { disabled: false, value: caseData.tobacco } : false),
      vaping: new FormControl(caseData ? { disabled: false, value: caseData.vaping } : false),
      tobaccoQuantity: new FormControl(caseData ? { disabled: false, value: caseData.tobaccoQuantity } : ''),
      vapingQuantity: new FormControl(caseData ? { disabled: false, value: caseData.vapingQuantity } : ''),
      duration: new FormControl(caseData ? { disabled: true, value: caseData.duration } : '', [Validators.required])
    });
    if (this.showBirthDate) {
      this.basicData.addControl(
        'birthDate',
        new FormControl(caseData ? { disabled: true, value: this.birthDate ? this.birthDate : caseData.birthDate } : '', [
          CalcValidators.age(18, 100),
          Validators.required
        ])
      );
      this.basicData.controls.birthDate.valueChanges.subscribe((value) => (this.birthDate = value));
    }
    if (this.formGroupDirective.form) {
      this.formGroupDirective.form.setControl('basicData', this.basicData);
    }

    this.basicData.controls.tobacco.valueChanges.subscribe((value) => {
      if (!value) {
        this.basicData.controls.tobaccoQuantity.reset();
      }
    });

    this.basicData.controls.vaping.valueChanges.subscribe((value) => {
      if (!value) {
        this.basicData.controls.vapingQuantity.reset();
      }
    });

    this.basicData.controls.gender.valueChanges.subscribe((value) => {
      this.calcIMC();
      if (this.formGroupDirective.form) {
        (this.formGroupDirective.form.controls.kidneyProfile as FormGroup).controls.creatinine.patchValue(
          (this.formGroupDirective.form.controls.kidneyProfile as FormGroup).controls.creatinine.value
        );
      }
    });

    this.basicData.controls.heightMetricSystem.valueChanges.pipe(debounceTime(500)).subscribe((value: string) => {
      if (value === this.translateService.instant('METABOLIC-CALC.SYS-METRIC')) {
        this.heightMeasures = ['m', 'cm'];
        if (caseData && caseData.heightMeasure) {
          this.basicData.controls.heightMeasure.setValue(caseData.heightMeasure);
        } else {
          this.basicData.controls.height.setValue('');
          this.basicData.controls.heightMeasure.setValue('');
        }
      } else {
        this.basicData.controls.height.setValue('');
        this.basicData.controls.heightMeasure.setValue('ft');
      }
    });

    this.basicData.controls.height.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.calcIMC();
    });
    this.basicData.controls.heightMeasure.valueChanges.pipe(debounceTime(500)).subscribe((value: string) => {
      // Funcionalidad original
      this.calcIMC();
      this.basicData.controls.height.updateValueAndValidity();
    });
    this.basicData.controls.weight.valueChanges.pipe(debounceTime(500)).subscribe(() => this.calcIMC());
    this.basicData.controls.weightMeasure.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.calcIMC();
      this.basicData.controls.weight.updateValueAndValidity();
    });

    this.basicData.controls.abdominalPerimeterMeasure.valueChanges.subscribe((value: string) => {
      this.basicData.controls.abdominalPerimeter.clearValidators();
      if (value === 'cm') {
        this.basicData.controls.abdominalPerimeter.addValidators([CalcValidators.abdominalPerimeter(35, 300, value)]);
      } else if (value === 'in') {
        this.basicData.controls.abdominalPerimeter.addValidators([CalcValidators.abdominalPerimeter(19, 119, value)]);
      } else {
        this.basicData.controls.abdominalPerimeter.addValidators([CalcValidators.abdominalPerimeter(0, 0, '')]);
      }
      this.basicData.controls.abdominalPerimeter.updateValueAndValidity();
    });

    this.formReady = true;
  }

  // Method which set age form from date change detected
  calcAge(data: MatDatepickerInputEvent<Date>) {
    const date = moment(data.value);
    this.basicData.get('age')?.setValue(moment().year() - date.year());
  }

  // Method to check if we have a case in progress assigned
  hasCaseInCourse(): boolean {
    return this.caseInProgressSrv.hasInCourse();
  }

  hasCaseInParams(): boolean {
    const idCaseValue = this.activatedRouter.snapshot.paramMap.get('idCase');
    // Devolvemos true si no es null o valor vacío
    return idCaseValue !== null && idCaseValue !== '';
  }

  calcIMC() {
    if (
      ((this.basicData.controls.heightMetricSystem.value === this.translateService.instant('METABOLIC-CALC.SYS-METRIC') &&
        this.basicData.controls.height.value &&
        this.basicData.controls.height.valid &&
        this.basicData.controls.heightMeasure.value) ||
        (this.basicData.controls.heightMetricSystem.value === this.translateService.instant('METABOLIC-CALC.SYS-IMPERIAL') &&
          this.basicData.controls.heightFt.valid &&
          this.basicData.controls.heightFt.value &&
          this.basicData.controls.heightInch.valid &&
          (this.basicData.controls.heightInch.value || this.basicData.controls.heightInch.value === 0))) &&
      this.basicData.controls.weight.value &&
      this.basicData.controls.weight.valid &&
      this.basicData.controls.weightMeasure.value
    ) {
      const spinner = this.spinnerService.showSpinner();
      const body = this.formGroupDirective?.form.getRawValue();
      delete body.basicData?.birthDate;
      this.metabolicoService.imcPost(body).subscribe(
        (response: Mensaje) => {
          if (response.value && typeof response.value === 'object') {
            this.basicData.controls.imc.setValue((response.value as any).imc);
          } else {
            this.basicData.controls.imc.setValue(response.value);
          }
          spinner.close();
        },
        (error) => {
          spinner.close();
          this.modalService.openModalDanger('Error', error);
        }
      );
    }
  }
}
